import React, { useState } from 'react';
import { Button, TextField, IconButton, Box, Paper } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';

interface SynonymsEditorProps {
    synonyms: { [key: string]: (number | string)[] };
    onChange: (synonyms: { [key: string]: (number | string)[] }) => void;
}

export const SynonymsEditor: React.FC<SynonymsEditorProps> = ({ synonyms, onChange }) => {
    const [newKey, setNewKey] = useState('');

    const handleAddKey = () => {
        if (newKey.trim() && !synonyms[newKey.trim()]) {
            onChange({
                ...synonyms,
                [newKey.trim()]: []
            });
            setNewKey('');
        }
    };

    const handleDeleteKey = (key: string) => {
        const newSynonyms = { ...synonyms };
        delete newSynonyms[key];
        onChange(newSynonyms);
    };

    const handleAddValue = (key: string) => {
        const newSynonyms = { ...synonyms };
        if (!newSynonyms[key]) {
            newSynonyms[key] = [];
        }
        newSynonyms[key] = [...newSynonyms[key], ''];
        onChange(newSynonyms);
    };

    const handleDeleteValue = (key: string, index: number) => {
        const newSynonyms = { ...synonyms };
        newSynonyms[key] = newSynonyms[key].filter((_, i) => i !== index);
        onChange(newSynonyms);
    };

    const handleValueChange = (key: string, index: number, value: string) => {
        const newSynonyms = { ...synonyms };
        // Если значение пустое, сохраняем пустую строку
        if (value.trim() === '') {
            newSynonyms[key][index] = '';
        } else {
            // Пытаемся определить, является ли значение числом
            const numValue = parseFloat(value);
            // Проверяем, является ли значение валидным числом (не NaN и совпадает с исходной строкой)
            if (!isNaN(numValue) && isFinite(numValue) && value.trim() === numValue.toString()) {
                // Сохраняем как число
                newSynonyms[key][index] = numValue;
            } else {
                // Сохраняем как строку
                newSynonyms[key][index] = value;
            }
        }
        onChange(newSynonyms);
    };

    return (
        <Box>
            {/* Добавление нового ключа */}
            <Box style={{ display: 'flex', gap: 10, marginBottom: 20, alignItems: 'center' }}>
                <TextField
                    label="Новый ключ"
                    value={newKey}
                    onChange={(e) => setNewKey(e.target.value)}
                    onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                            handleAddKey();
                        }
                    }}
                    variant="outlined"
                    size="small"
                    style={{ flex: 1 }}
                />
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleAddKey}
                    style={{ backgroundColor: "#d1a0a9" }}
                    disabled={!newKey.trim() || !!synonyms[newKey.trim()]}
                >
                    Добавить ключ
                </Button>
            </Box>

            {/* Список ключей и их значений */}
            {Object.keys(synonyms).length === 0 ? (
                <Box style={{ padding: 20, textAlign: 'center', color: '#999' }}>
                    Нет ключей. Добавьте новый ключ для начала работы.
                </Box>
            ) : (
                <Box>
                    {Object.keys(synonyms).map((key) => (
                        <Paper
                            key={key}
                            style={{
                                padding: 15,
                                marginBottom: 15,
                                border: '1px solid #e0e0e0',
                                borderRadius: 4
                            }}
                        >
                            <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                                <Box style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                    <strong style={{ fontSize: 16 }}>{key}</strong>
                                    <span style={{ color: '#666', fontSize: 12 }}>
                                        (массив чисел или строк)
                                    </span>
                                </Box>
                                <IconButton
                                    color="error"
                                    onClick={() => handleDeleteKey(key)}
                                    size="small"
                                    aria-label="delete key"
                                >
                                    <DeleteIcon />
                                </IconButton>
                            </Box>

                            {/* Значения массива */}
                            <Box style={{ marginLeft: 20 }}>
                                {synonyms[key].length === 0 ? (
                                    <Box style={{ color: '#999', fontSize: 14, marginBottom: 10 }}>
                                        Массив пуст
                                    </Box>
                                ) : (
                                    synonyms[key].map((value, index) => (
                                        <Box
                                            key={index}
                                            style={{
                                                display: 'flex',
                                                gap: 10,
                                                alignItems: 'center',
                                                marginBottom: 10
                                            }}
                                        >
                                            <TextField
                                                value={value}
                                                onChange={(e) => handleValueChange(key, index, e.target.value)}
                                                variant="outlined"
                                                size="small"
                                                style={{ width: 200 }}
                                                placeholder="Введите число или текст"
                                            />
                                            <IconButton
                                                color="error"
                                                onClick={() => handleDeleteValue(key, index)}
                                                size="small"
                                                aria-label="delete value"
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Box>
                                    ))
                                )}
                                <Button
                                    variant="outlined"
                                    startIcon={<AddIcon />}
                                    onClick={() => handleAddValue(key)}
                                    size="small"
                                    style={{ marginTop: 5 }}
                                >
                                    Добавить значение
                                </Button>
                            </Box>
                        </Paper>
                    ))}
                </Box>
            )}
        </Box>
    );
};
